import { useLingui } from '@lingui/react/macro';
import type { FieldValues } from 'react-hook-form';

import type { ActionDefinition } from '@/shared/action';
import { notifyApiError, useNotifier } from '@/shared/data';
import { resolvePatternText } from '@/shared/patterns';
import { toast } from '@/shared/ui/toaster';

// A execução de uma ferramenta do agente pela definição da tela (B3 da !11138): a mesma validação do
// formulário, o mesmo bloqueio, a mesma mutação (que invalida o cache) e o mesmo toast do diálogo.

/* eslint-disable lingui/no-unlocalized-strings -- respostas ao modelo, inglês canônico (U33) */
export const RUNNER_MODEL = {
  invalid: (issues: string) => `The input does not pass the screen form: ${issues}. Nothing was changed.`,
  blocked: (reason: string) => `The screen blocks this action: ${reason} Nothing was changed.`,
};
/* eslint-enable lingui/no-unlocalized-strings */

export type DefinitionRunner = <C, V extends FieldValues, R>(def: ActionDefinition<C, V, R>, values: V, ctx: C) => Promise<R>;

/** Valida como o formulário da tela, executa pela definição dela e avisa como o diálogo (`ActionForm.celebrate`). */
export function useDefinitionRunner(): DefinitionRunner {
  const { i18n, t } = useLingui();
  const notifier = useNotifier();
  return async (def, values, ctx) => {
    const blocked = def.blocked?.(ctx);
    if (blocked) throw new Error(RUNNER_MODEL.blocked(resolvePatternText(i18n, blocked)));
    const parsed = def.schema ? def.schema(ctx, i18n).safeParse(values) : { success: true as const, data: values };
    if (!parsed.success) throw new Error(RUNNER_MODEL.invalid(parsed.error.issues.map((issue) => issue.message).join('; ')));
    try {
      const result = await def.submit(parsed.data, ctx);
      if (def.success !== false) {
        const message = def.success?.(result, parsed.data, ctx);
        const title = resolvePatternText(i18n, def.title(ctx));
        const heading = message ? resolvePatternText(i18n, message.title) : t`Feito: ${title}`;
        const description = message?.description ? resolvePatternText(i18n, message.description) : def.record(ctx);
        toast.success(heading, description ? { description } : undefined);
      }
      return result;
    } catch (error) {
      notifyApiError(notifier, error);
      throw error;
    }
  };
}
